<template>
  <div class="dashboard-body-futuristic">
    <main class="futuristic-container">
      <!-- Sidebar -->
      <AppSidebar activeItem="tenders" />

      <!-- Main Content Area -->
      <section class="dashboard-card-futuristic dashboard-card-scrollable">
  <div class="space-y-4 animate-slide-up text-left">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1>Tenders</h1>
        <p class="text-slate-500 text-sm mt-0.5">
          <span v-if="loading">Loading…</span>
          <span v-else>
            <span class="font-semibold text-slate-900">{{ total.toLocaleString("en-IN") }}</span>
            tenders found
            <span v-if="closingSoonActive" class="text-amber-700 font-medium ml-1">closing in the next 3 days</span>
            <span v-else-if="hasActiveFilters" class="text-primary-600 ml-1">(filtered)</span>
          </span>
        </p>
      </div>

      <div class="flex items-center gap-3">
        <button
          v-if="isLoggedIn()"
          @click="onExport"
          :disabled="loading || !tenders.length"
          class="btn-primary export-btn flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowDownTrayIcon class="w-3 h-3" aria-hidden="true" />
          Export Page to Excel
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div v-if="statsLoading" class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div v-for="i in 3" :key="i" class="stat-skeleton">
        <div class="stat-skeleton__top">
          <div class="stat-skeleton__icon" />
        </div>
        <div class="stat-skeleton__label" />
        <div class="stat-skeleton__value" />
        <div class="stat-skeleton__sub" />
      </div>
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <StatCard
        label="Total Tenders"
        :value="stats.total_tenders || 0"
        :icon="DocumentTextIcon"
        subtitle="Across all portals"
        color="blue"
      />
      <StatCard
        label="Active Tenders"
        :value="stats.active_tenders || 0"
        :icon="CheckCircleIcon"
        subtitle="Currently accepting bids"
        color="emerald"
      />
      <!-- The deadline is what this page is for, so Closing Soon is the one
           card that acts: it filters the list to those tenders, soonest first. -->
      <StatCard
        label="Closing Soon"
        :value="stats.closing_soon || 0"
        :icon="ClockIcon"
        subtitle="Closing within 3 days"
        color="amber"
        pressable
        :pressed="closingSoonActive"
        action-label="Show these tenders"
        pressed-action-label="Show all tenders"
        @press="toggleClosingSoon"
      />
    </div>

    <!-- Search + Filters in one row -->
    <div class="card p-3 flex flex-wrap items-center gap-3 animate-fade-in">
      <!-- Search -->
      <div class="relative flex-1 min-w-[200px] flex items-center gap-2">
        <MagnifyingGlassIcon class="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden="true" />
        <input
          v-model="searchInput"
          @input="debouncedSearch"
          type="text"
          enterkeyhint="search"
          aria-label="Search tenders"
          placeholder="Search by Tender ID, title, reference, or organisation"
          class="flex-1 text-sm text-slate-700 placeholder-slate-500 bg-transparent border-none outline-none"
        />
        <button
          v-if="searchInput"
          @click="clearSearch"
          aria-label="Clear search"
          class="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
        >
          <XMarkIcon class="w-4 h-4 text-slate-500" aria-hidden="true" />
        </button>
      </div>

      <!-- Divider between search and filters -->
      <div class="w-px h-5 bg-slate-200 flex-shrink-0"></div>

      <!-- Filters inline -->
      <FilterPanel
        v-model="filters"
        :portals="portals"
        :listing-types="listingTypes"
        @change="onFilterChange"
        :inline="true"
      />

      <!-- Divider before view toggle -->
      <div class="w-px h-5 bg-slate-200 flex-shrink-0"></div>

      <!-- View toggle -->
      <div class="flex items-center gap-0.5 flex-shrink-0" role="group" aria-label="Layout">
        <button
          @click="viewMode = 'table'"
          class="p-1.5 rounded-md transition-all"
          :class="viewMode === 'table' ? 'text-slate-700 bg-slate-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'"
          title="Table view"
          aria-label="Table view"
          :aria-pressed="viewMode === 'table'"
        >
          <TableCellsIcon class="w-4 h-4" aria-hidden="true" />
        </button>
        <button
          @click="viewMode = 'cards'"
          class="p-1.5 rounded-md transition-all"
          :class="viewMode === 'cards' ? 'text-slate-700 bg-slate-100' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'"
          title="Card view"
          aria-label="Card view"
          :aria-pressed="viewMode === 'cards'"
        >
          <Squares2X2Icon class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !tenders.length">
      <div v-if="viewMode === 'table'" class="card p-6 animate-pulse h-96" />
      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div v-for="i in 6" :key="i" class="card p-5 animate-pulse h-44" />
      </div>
    </div>

    <!-- Table view (default) -->
    <TenderTable
      v-else-if="tenders.length && viewMode === 'table'"
      :tenders="tenders"
      :highlight="appliedSearch"
    />

    <!-- Card view -->
    <div
      v-else-if="tenders.length && viewMode === 'cards'"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
    >
      <TenderCard
        v-for="tender in tenders"
        :key="tender.id"
        :tender="tender"
        :highlight="appliedSearch"
      />
    </div>

    <!-- Load failure — kept apart from the empty state so a network error
         isn't reported as "no matching tenders". -->
    <div v-else-if="loadError" class="card p-12 text-center" role="alert">
      <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
        <ExclamationTriangleIcon class="w-10 h-10 text-slate-500" aria-hidden="true" />
      </div>
      <h3 class="text-lg font-semibold text-slate-900 mb-1">Couldn't Load Tenders</h3>
      <p class="text-sm text-slate-500 mb-4">
        Check your connection, then try again.
      </p>
      <button @click="loadTenders" class="btn-ghost">Try Again</button>
    </div>

    <!-- Empty state -->
    <div v-else class="card p-12 text-center">
      <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
        <InboxIcon class="w-10 h-10 text-slate-500" aria-hidden="true" />
      </div>
      <h3 class="text-lg font-semibold text-slate-900 mb-1">No Tenders Found</h3>
      <p class="text-sm text-slate-500 mb-4">
        Try adjusting your filters or search query.
      </p>
      <button @click="resetAll" class="btn-ghost">Clear All Filters</button>
    </div>

    <!-- Pagination -->
    <Pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @change="changePage"
    />
  </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useDebounceFn } from "@vueuse/core";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  InboxIcon,
  TableCellsIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";

import AppSidebar from "./Sidebar.vue";
import StatCard from "./StatCard.vue";
import TenderCard from "./TenderCard.vue";
import TenderTable from "./TenderTable.vue";
import FilterPanel from "./FilterPanel.vue";
import Pagination from "./Pagination.vue";
import { tenderApi } from "../api/tender";
import { exportTendersToExcel } from "../utils/exportExcel";
import { isLoggedIn } from "../utils/auth";
import { CLOSING_SOON_DAYS } from "../utils/dateUtils";

const route   = useRoute();
const router  = useRouter();
const toast   = useToast();

const tenders     = ref([]);
const portals     = ref([]);
const listingTypes = ref([]);
const total       = ref(0);
const totalPages  = ref(0);
const currentPage = ref(1);
const loading     = ref(true);
const loadError   = ref(false);
// The term the visible results were fetched with. Highlighting follows this,
// not the live input, so marks always match the rows on screen.
const appliedSearch = ref("");
const stats        = ref({});
const statsLoading = ref(true);
const limit       = 20;

const viewMode = ref(localStorage.getItem("tender_view_mode") || "table");
watch(viewMode, (val) => localStorage.setItem("tender_view_mode", val));

const searchInput = ref(route.query.search || "");
const filters = ref({
  portal:         route.query.portal || "",
  status:         route.query.status || "",
  listing_type:   route.query.listing_type || "",
  closing_before: route.query.closing_before || "",
  closing_within_days: route.query.closing_within_days || "",
  sort_by:        route.query.sort_by || "published_date",
});

const hasActiveFilters = computed(() =>
  filters.value.portal || filters.value.status || filters.value.listing_type ||
  filters.value.closing_before || filters.value.closing_within_days ||
  searchInput.value
);

const closingSoonActive = computed(() =>
  String(filters.value.closing_within_days) === String(CLOSING_SOON_DAYS)
);

function toggleClosingSoon() {
  if (closingSoonActive.value) {
    filters.value = { ...filters.value, closing_within_days: "", sort_by: "published_date" };
  } else {
    filters.value = { ...filters.value, closing_within_days: CLOSING_SOON_DAYS, sort_by: "closing_date" };
  }
  onFilterChange();
}

async function loadPortals() {
  try {
    const res = await tenderApi.listPortals();
    portals.value = res.portals || [];
  } catch (e) {
    console.error(e);
  }
}

// The Type dropdown offers what the selected portal actually publishes, so the
// facets are re-read whenever the portal changes. A failure leaves the list
// empty, which hides the dropdown rather than showing a broken one.
let latestFacets = 0;
async function loadFacets() {
  const requestId = ++latestFacets;
  try {
    const params = filters.value.portal ? { portal: filters.value.portal } : {};
    const res = await tenderApi.listFacets(params);
    if (requestId !== latestFacets) return;
    listingTypes.value = res.listing_types || [];
  } catch (e) {
    if (requestId !== latestFacets) return;
    listingTypes.value = [];
    console.error(e);
  }
}

async function loadStats() {
  statsLoading.value = true;
  try {
    stats.value = await tenderApi.getStats();
  } catch (e) {
    console.error(e);
    stats.value = {};
  } finally {
    statsLoading.value = false;
  }
}

// The search term this page last wrote into the URL. The route watcher below
// skips that echo; reacting to it overwrote the input with an older, trimmed
// value while the user was still typing, so fast typing came out jumbled.
let syncedSearch = searchInput.value.trim();
// Only the newest request may update the list: a slow response for "ab" must
// not replace the results for "abc".
let latestRequest = 0;

async function loadTenders() {
  const requestId = ++latestRequest;
  loading.value = true;
  loadError.value = false;
  try {
    const params = {
      page:  currentPage.value,
      limit,
      ...filters.value,
    };
    if (searchInput.value.trim()) params.search = searchInput.value.trim();

    Object.keys(params).forEach((k) => {
      if (params[k] === "" || params[k] === null) delete params[k];
    });

    syncedSearch = params.search || "";
    router.replace({ query: params });

    const res = await tenderApi.listTenders(params);
    if (requestId !== latestRequest) return;
    tenders.value    = res.tenders || [];
    appliedSearch.value = params.search || "";
    total.value      = res.total || 0;
    totalPages.value = res.total_pages || 0;
  } catch (e) {
    if (requestId !== latestRequest) return;
    tenders.value = [];
    loadError.value = true;
    toast.error(`Failed to load tenders: ${e.message}`);
  } finally {
    if (requestId === latestRequest) loading.value = false;
  }
}

const debouncedSearch = useDebounceFn(() => {
  currentPage.value = 1;
  loadTenders();
}, 400);

// The portal the facets were last read for, so changing any other filter does
// not re-request a list that cannot have changed.
let facetsPortal = filters.value.portal;

function onFilterChange() {
  currentPage.value = 1;
  if (filters.value.portal !== facetsPortal) {
    facetsPortal = filters.value.portal;
    loadFacets();
  }
  loadTenders();
}

function changePage(page) {
  currentPage.value = page;
  loadTenders();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function clearSearch() {
  searchInput.value = "";
  currentPage.value = 1;
  loadTenders();
}

function resetAll() {
  searchInput.value = "";
  filters.value = {
    portal: "", status: "", listing_type: "", closing_before: "",
    closing_within_days: "", sort_by: "published_date",
  };
  currentPage.value = 1;
  if (facetsPortal !== "") {
    facetsPortal = "";
    loadFacets();
  }
  loadTenders();
}

async function onExport() {
  try {
    await exportTendersToExcel(
      tenders.value,
      `tenders_page${currentPage.value}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    toast.success("Excel file downloaded");
  } catch (e) {
    toast.error(`Export failed: ${e.message}`);
  }
}

// Only searches started elsewhere (a link carrying ?search=) reach this; the
// page's own URL updates are filtered out by syncedSearch.
watch(() => route.query.search, (val) => {
  const incoming = val || "";
  if (incoming === syncedSearch) return;
  syncedSearch = incoming;
  searchInput.value = incoming;
  currentPage.value = 1;
  loadTenders();
});

onMounted(async () => {
  loadStats();
  loadFacets();
  await loadPortals();
  await loadTenders();
});
</script>

<style scoped>
/* ── Export button ── */
.export-btn {
  border-radius: 10px;
  padding: 8px 14px !important;
  font-size: 13px !important;
  line-height: 1 !important;
}

/* ── Stat card loading skeleton ── */
@keyframes skeleton-wave {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
.stat-skeleton {
  border-radius: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
  min-height: 148px;
}
.stat-skeleton__top  { margin-bottom: 20px; }
.stat-skeleton__icon,
.stat-skeleton__label,
.stat-skeleton__value,
.stat-skeleton__sub {
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: skeleton-wave 1.6s ease-in-out infinite;
}
.stat-skeleton__icon  { width: 48px; height: 48px; border-radius: 12px; }
.stat-skeleton__label { width: 60%;  height: 12px; margin-bottom: 10px; }
.stat-skeleton__value { width: 40%;  height: 32px; margin-bottom: 12px; border-radius: 6px; }
.stat-skeleton__sub   { width: 75%;  height: 10px; }
</style>
